import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CLINICAL_REASON_LIMITS } from '@optiflow/types';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormField';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/utils/cn';

interface ReasonDialogProps {
  title: string;
  /** What the change does and does not do, one plain sentence each. */
  points: string[];
  reasonLabel: string;
  confirmLabel: string;
  isDestructive?: boolean;
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

/**
 * Confirms a correction of the clinical record — an amendment, a void, a discard — with the reason the record keeps.
 *
 * The reason is never prefilled: what the user writes is what the record says. Mount it only while it is open, so every opening starts empty and the page never holds two dialogs at once.
 */
export function ReasonDialog({
  title,
  points,
  reasonLabel,
  confirmLabel,
  isDestructive = false,
  isSubmitting,
  onCancel,
  onConfirm,
}: ReasonDialogProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [attempted, setAttempted] = useState(false);
  const { min, max } = CLINICAL_REASON_LIMITS;
  const trimmed = reason.trim();
  const tooShort = trimmed.length < min;
  const showError = attempted && tooShort;

  const submit = () => {
    setAttempted(true);
    if (!tooShort) onConfirm(trimmed);
  };

  return (
    <Modal
      isOpen
      onClose={isSubmitting ? () => undefined : onCancel}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button
            variant={isDestructive ? 'danger' : 'primary'}
            onClick={submit}
            isLoading={isSubmitting}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <ul className="text-ink list-disc space-y-1.5 ps-5 text-sm">
          {points.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
        <FormField
          label={reasonLabel}
          required
          hint={t('medical.lifecycle.reasonHint', { min })}
          error={showError ? t('medical.lifecycle.reasonRequired', { min }) : undefined}
        >
          {({ id, describedBy }) => (
            <textarea
              id={id}
              aria-describedby={describedBy}
              aria-invalid={showError || undefined}
              value={reason}
              maxLength={max}
              rows={3}
              onChange={(event) => setReason(event.target.value)}
              className={cn(
                'w-full rounded-lg border bg-white px-3 py-2.5 text-sm',
                'focus:border-brand-500 focus:ring-brand-100 focus:ring-4 focus:outline-none',
                showError ? 'border-red-500' : 'border-line',
              )}
            />
          )}
        </FormField>
      </div>
    </Modal>
  );
}
